/** Filtros combináveis da Expedição (chips). Abrem em Atrasados + Hoje (FILTRO_INICIAL). */
import type { ReactNode } from 'react'
import { Box, Button, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material'
import type { Alerta, Canal, Faixa, Pedido, Situacao } from './types'
import { CANAIS, ROTULO_CANAL } from './types'
import {
  ORDEM_SITUACAO, ROTULO_ALERTA, ROTULO_FAIXA, ROTULO_SITUACAO, ROTULO_STATUS_MK, SEM_EMBALADOR, SEM_STATUS_MK,
  FILTRO_VAZIO, contaFiltros, filtrosOpcionais, type Filtro,
} from './logic'

type ChaveSet = 'canal' | 'faixa' | 'situacao' | 'statusMk' | 'embalador' | 'alerta'

const FAIXAS: Faixa[] = ['atrasado', 'hoje', 'amanha', 'proximos', 'sem_prazo']
const ALERTAS: Alerta[] = ['atrasado', 'aguardando_liberacao_hoje', 'cancelado_mk', 'travado', 'sem_prazo', 'parado_tiny']
const STATUS_MK = ['a_enviar', 'cancelado', 'enviado', SEM_STATUS_MK]

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 1.5 }} alignItems={{ sm: 'center' }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>{rotulo}</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ flex: 1 }}>{children}</Stack>
    </Stack>
  )
}

/** `pedidos` = dados.pedidos (lista inteira): os chips do F4 (Shopee Decora, Aguardando liberação e o alerta dele) só
 * aparecem com dado — flags desligadas = filtros de antes. */
export function Filtros({ filtro, onChange, embaladores, pedidos = [] }: {
  filtro: Filtro; onChange: (f: Filtro) => void; embaladores: { id: string; nome: string }[]; pedidos?: Pedido[]
}) {
  const op = filtrosOpcionais(pedidos, filtro)
  const canais = CANAIS.filter((c) => c !== 'shopee_decora' || op.decora)
  const situacoes = ORDEM_SITUACAO.filter((s) => s !== 0 || op.aguardando)
  const alertas = ALERTAS.filter((a) => a !== 'aguardando_liberacao_hoje' || op.alertaAguardando)
  function chip<T>(chave: ChaveSet, valor: T, rotulo: string, alerta = false) {
    const set = filtro[chave] as Set<T>
    const on = set.has(valor)
    const alternar = () => {
      const novo = new Set(set)
      if (on) novo.delete(valor)
      else novo.add(valor)
      // faixa e intervalo de datas são excludentes (juntos dariam lista vazia)
      onChange(chave === 'faixa' ? { ...filtro, faixa: novo as Set<Faixa>, de: undefined, ate: undefined } : { ...filtro, [chave]: novo })
    }
    return (
      <Chip key={String(valor)} size="small" label={rotulo} onClick={alternar}
        variant={on ? 'filled' : 'outlined'} color={on ? (alerta ? 'error' : 'primary') : 'default'} />
    )
  }
  const n = contaFiltros(filtro)

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1}>
          <Linha rotulo="Marketplace">
            {canais.map((c) => chip<Canal>('canal', c, ROTULO_CANAL[c]))}
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="outlined" disabled={!n} onClick={() => onChange(FILTRO_VAZIO)}>Limpar filtros ({n})</Button>
          </Linha>
          <Linha rotulo="Data máx. de envio">
            {FAIXAS.map((f) => chip<Faixa>('faixa', f, ROTULO_FAIXA[f]))}
            <TextField type="date" size="small" label="de" value={filtro.de ?? ''} sx={{ width: 150, ml: 1 }}
              slotProps={{ inputLabel: { shrink: true } }} onChange={(e) => onChange({ ...filtro, faixa: new Set(), de: e.target.value || undefined })} />
            <TextField type="date" size="small" label="até" value={filtro.ate ?? ''} sx={{ width: 150 }}
              slotProps={{ inputLabel: { shrink: true } }} onChange={(e) => onChange({ ...filtro, faixa: new Set(), ate: e.target.value || undefined })} />
          </Linha>
          <Linha rotulo="Situação separação">
            {situacoes.map((s) => chip<Situacao>('situacao', s, ROTULO_SITUACAO[s]))}
          </Linha>
          <Linha rotulo="Status no marketplace">
            {STATUS_MK.map((s) => chip<string>('statusMk', s, ROTULO_STATUS_MK[s]))}
          </Linha>
          <Linha rotulo="Usuário embalador">
            {embaladores.map((e) => chip<string>('embalador', e.id, e.nome))}
            {chip<string>('embalador', SEM_EMBALADOR, 'Sem embalador')}
          </Linha>
          <Linha rotulo="Pontos de atenção">
            {alertas.map((a) => chip<Alerta>('alerta', a, ROTULO_ALERTA[a], true))}
          </Linha>
        </Stack>
      </CardContent>
    </Card>
  )
}
